import { useEffect, useState } from "react";

import ko from "knockout";

import { useMemoOne } from "@services/useMemoOne";

import type { ComputedValuePair } from "./types";

/**
 * Hook for creating a knockout pureComputed inline and subscribing to its
 * value. Takes a dependency array which will invalidate the compute function.
 * @deprecated please migrate away from knockout
 */
export const useKoComputed = <V>(computeFunc: () => V, dependencies: unknown[]): V => {
  // We have to jump through some hoops here to make it so that updating the observable or
  // the dependency array only triggers one re-render. Current will always
  // contain the current computed and its initial value, subscribed gets updated
  // only when the current computed is updated.
  const current: ComputedValuePair<V> = useMemoOne(() => {
    const computed = ko.pureComputed(computeFunc).extend({ defer: true });
    return { computed, value: computed.peek() };
  }, dependencies);

  const [subscribed, setSubscribed] = useState<ComputedValuePair<V>>(current);
  useEffect(() => {
    const { computed } = current;
    const subscription = computed.subscribe((value) => setSubscribed({ value, computed }));
    // Unlikely but may be possible that an update has come in since render but
    // before subscribe, so double check to make sure.
    if (current.value !== computed.peek()) {
      setSubscribed({ computed, value: computed.peek() });
    }
    return () => {
      subscription.dispose();
    };
  }, [current]);

  return current.computed === subscribed.computed ? subscribed.value : current.value;
};
